import "./WorkflowTemplateStep.css";

import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { SortColumn } from "react-data-grid";

import { EditGreyIcon, MandatoryFlagIcon, MilestoneDiamondIcon, TrashDark } from "../../../../../../../../../assets";
import { WorkflowTemplateOrganisationAccessLevel } from "../../../../../../../../../constants";
import {
  PHASE_BOUNDARY_KEYS,
  phaseBoundaryDeadlineLabel,
} from "../../../../../../../../../constants/project-dates/projectDateFieldKeys";
import {
  CursorChangeProps,
  ResultType,
  SearchWorkflowTemplateTasksResponseTask,
} from "../../../../../../../../../models";
import { SearchWorkflowTemplateTasksResponse } from "../../../../../../../../../service/query";
import { searchWorkflowTemplateTasks } from "../../../../../../../../../service/query/QueryService.full";
import { ResultData, Status } from "../../../../../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../../../../../service/ValidationErrorFormatter";
import { reorderWorkflowTemplateStep } from "../../../../../../../../../service/workflow";
import {
  convertDaysToYearsMonthsWeeksAndDays,
  flattenObject,
  stringKeyToSentenceCase,
} from "../../../../../../../../../utils";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  dataGridMapFilterCriteria,
  ProfileMenuItemType,
  Toast,
} from "../../../../../../../../../widget";
import { IconNoActionCellFormatterData } from "../../../../../../../../../widget/data/DataGrid/models";
import { WorkflowTemplateContext } from "../../../../WorkflowTemplateContext";
import { WorkflowTemplateStepModel } from "../../models";

interface UseWorkflowTemplateStepProps {
  workflowTemplateStep: WorkflowTemplateStepModel;
}
interface UseWorkflowTemplateStepReturnData {
  showStep: boolean;
  handleShowStep: (show: boolean) => void;

  workflowTemplateUuid: string | undefined;
  stepActionsMenuOptionsList: ProfileMenuItemType[];

  defaultSortingCriteria: SortColumn[];
  columns: DataGridColumnDefinition[];
  onChange: ({ filtering, paging }: CursorChangeProps) => Promise<ResultType>;
  isLoadingTasks: boolean;

  showTaskPanel: boolean;
  setShowTaskPanel: Dispatch<SetStateAction<boolean>>;

  showStepActionsMenu: boolean;
  setShowStepActionsMenu: Dispatch<SetStateAction<boolean>>;

  showDeleteTemplateStepModal: boolean;
  setShowDeleteTemplateStepModal: Dispatch<SetStateAction<boolean>>;

  showEditTemplateStepModal: boolean;
  setShowEditTemplateStepModal: Dispatch<SetStateAction<boolean>>;

  templateTask: SearchWorkflowTemplateTasksResponseTask | undefined;
  handleCloseTemplateTaskPanel: () => void;

  refreshWorkflowTemplate: boolean;
  onStepDrop: (droppedOnStepSequence: number, draggedStepUuid: string) => void;

  isContributorAndCanAddTasks: boolean;
  canEditStep: boolean;
}

const PAGE_SIZE = 7;

const columns: DataGridColumnDefinition[] = [
  {
    name: "Task",
    key: "defaultNameLink",
    dataType: "string",
    formatter: "buttonLink",
    minWidth: 300,
    filterable: false,
    sortable: false,
  },
  {
    name: "Owner",
    key: "ownerOrganisationType",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 140,
    filterable: false,
    sortable: false,
  },
  {
    name: "Deadline",
    key: "customDeadline",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 250,
    filterable: false,
    sortable: false,
  },
  {
    name: "Visibility",
    key: "defaultVisibility",
    dataType: "string",
    formatter: "stringWithEllipsis",
    minWidth: 160,
    filterable: false,
    sortable: false,
  },
  {
    name: "Milestone",
    key: "defaultIsMilestoneIcon",
    dataType: "string",
    formatter: "iconNoAction",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 90,
  },
  {
    name: "Mandatory",
    key: "defaultIsMandatoryIcon",
    dataType: "boolean",
    formatter: "iconNoAction",
    alignment: "center",
    filterable: false,
    sortable: false,
    minWidth: 90,
  },
];

const defaultSortingCriteria: SortColumn[] = [{ columnKey: "defaultDeadlineField", direction: "DESC" }];

export const useWorkflowTemplateStep = ({
  workflowTemplateStep,
}: UseWorkflowTemplateStepProps): UseWorkflowTemplateStepReturnData => {
  const {
    hasAccessLevelAndCanEditTemplate,
    canEditStepOrTask,
    shouldRefreshWorkflowTemplate,
    workflowTemplateUuid,
    refreshWorkflowTemplate,
  } = useContext(WorkflowTemplateContext);
  const [showStep, setShowStep] = useState(false);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [canEditStep, setCanEditStep] = useState(false);

  // For create / update task panel
  const [templateTask, setTemplateTask] = useState<SearchWorkflowTemplateTasksResponseTask>();
  const [showTaskPanel, setShowTaskPanel] = useState(false);
  const [showStepActionsMenu, setShowStepActionsMenu] = useState(false);
  const [showDeleteTemplateStepModal, setShowDeleteTemplateStepModal] = useState(false);
  const [showEditTemplateStepModal, setShowEditTemplateStepModal] = useState(false);

  const stepActionsMenuOptionsList: ProfileMenuItemType[] = [
    {
      id: 1,
      value: "Edit step",
      icon: <EditGreyIcon />,
      action: () => setShowEditTemplateStepModal(true),
    },
    {
      id: 2,
      value: "Delete step",
      icon: <TrashDark />,
      action: () => setShowDeleteTemplateStepModal(true),
    },
  ];

  const handleCloseTemplateTaskPanel = (): void => {
    setTemplateTask(undefined);
    setShowTaskPanel(false);
  };

  const handleShowStep = (show: boolean): void => {
    setShowStep(show);
  };

  const onStepDrop = async (droppedOnStepSequence: number, draggedStepUuid: string): Promise<void> => {
    if (draggedStepUuid !== workflowTemplateStep.uuid) {
      // Call reorderWorkflowTemplateStep here
      const res = await reorderWorkflowTemplateStep({
        workflowStep: null,
        workflowTemplateStep: {
          defaultSequence: droppedOnStepSequence,
          workflowTemplateStepUuid: draggedStepUuid,
        },
      });
      if (res.status === Status.Success) {
        // Handle success
        Toast.success({ message: "Template step successfully reordered" });
        shouldRefreshWorkflowTemplate();
      }
      if (res.status === Status.Error && res.errors) {
        res.errors.forEach((error) => {
          // Handle error
          Toast.error({ message: getErrorMessageFromCode(error.message) });
        });
      }
    }
  };

  const formatData = useCallback(
    (responseData: SearchWorkflowTemplateTasksResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        if (typeof result.defaultDeadlineValue === "number") {
          // A boundary key (start / end / overallStart / overallEnd) names the step phase's own
          // start or end, so its label has to be built from the pair - otherwise every task in the
          // template reads as the same bare "start". Anything else is a key in its own right.
          const field = typeof result.defaultDeadlineField === "string" ? result.defaultDeadlineField : "";
          const deadlineField = PHASE_BOUNDARY_KEYS.has(field)
            ? phaseBoundaryDeadlineLabel(workflowTemplateStep.defaultPhase, field)
            : stringKeyToSentenceCase(field);
          // eslint-disable-next-line no-nested-ternary
          result.customDeadline = `${deadlineField} ${result.defaultDeadlineValue === 0 ? "" : result.defaultDeadlineValue < 0 ? "-" : "+"} ${convertDaysToYearsMonthsWeeksAndDays(result.defaultDeadlineValue)}`;
        }

        result.defaultVisibility = el.defaultVisibility.join(", ");
        if (result.defaultIsMilestone) {
          result.defaultIsMilestoneIcon = {
            icon: <MilestoneDiamondIcon className="MilestoneDiamondIcon Active" width={20} height={20} />,
          } as IconNoActionCellFormatterData;
        }
        if (result.defaultIsMandatory) {
          result.defaultIsMandatoryIcon = {
            icon: <MandatoryFlagIcon className="MandatoryFlagIcon Active" width={20} height={20} />,
          } as IconNoActionCellFormatterData;
        }
        result.defaultNameLink = {
          text: el.defaultName,
          action: () => {
            setTemplateTask(el);
            setShowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;
        return result;
      }) || [],
    [workflowTemplateStep.defaultPhase]
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize: PAGE_SIZE,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    if (filterCriteria.defaultNameLink) {
      filterCriteria.defaultName = filterCriteria.defaultNameLink;
      filterCriteria.defaultNameLink = undefined;
      delete filterCriteria.defaultNameLink;
    }

    const sortKeyMap: Record<string, string> = {
      defaultNameLink: "defaultName",
    };

    filterCriteria.defaultStep = {
      uuid: {
        operator: "eq",
        value: workflowTemplateStep.uuid,
      },
    };

    await searchWorkflowTemplateTasks({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: PAGE_SIZE,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: PAGE_SIZE,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsLoadingTasks(false);
      });

    return data;
  };

  useEffect(() => {
    canEditStepOrTask(workflowTemplateStep.createdByOrganisationUuid).then((canEdit) => setCanEditStep(canEdit));
  }, [workflowTemplateStep.createdByOrganisationUuid]);

  return {
    showStep,
    handleShowStep,

    stepActionsMenuOptionsList,
    workflowTemplateUuid,

    onChange,
    isLoadingTasks,
    columns,
    defaultSortingCriteria,

    showTaskPanel,
    setShowTaskPanel,

    showDeleteTemplateStepModal,
    setShowDeleteTemplateStepModal,

    showEditTemplateStepModal,
    setShowEditTemplateStepModal,

    showStepActionsMenu,
    setShowStepActionsMenu,
    templateTask,
    handleCloseTemplateTaskPanel,
    onStepDrop,
    refreshWorkflowTemplate,
    isContributorAndCanAddTasks: hasAccessLevelAndCanEditTemplate(WorkflowTemplateOrganisationAccessLevel.Contributor),
    canEditStep,
  };
};
